import { useEffect, useMemo, useState } from 'react';
import { SettingClient } from '../../../helpers/client';
import { useLocation } from 'react-router';
import { useAppDispatch } from '../../../redux/hooks';
import { appStyleMode, basePath } from '../../../helpers/clientConfigs';
import * as externalLogin from '../../../helpers/externalLogin';
import { Spinner } from '../../spinner/spinner';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import { AppStyle } from '../../../helpers/appStyle';
import { authenticateFromFacebook } from '../../../redux/reducers/authReducer';
import { SignInProps } from '../../../interfaces/interfaces';
import { setRedirectAfterLogin } from '../../../helpers/localStorageService';
import { ExternalAuthenticationRequest } from 'orderme-api-integration-client';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import facebook from '../../../logo/facebook.png';

export function FacebookSignIn(props: SignInProps) {
  const dispatch = useAppDispatch();
  const routeNavigator = useRouteNavigator();
  const location = useLocation();
  const [clientId, setClientId] = useState('');
  const settingClient: SettingClient = useMemo(() => new SettingClient(), []);
  const [isBusy, setIsBusy] = useState(false);
  useTranslation('account');

  const handleFacebookSignIn = async (accessToken?: string, state?: string) => {
    const signInState = externalLogin.getFacebookState();
    externalLogin.removeFacebookState();

    if (!accessToken || state !== signInState) {
      if (props.onError) {
        props.onError('Facebook OAuth signIn failed');
      }
      return;
    }

    try {
      const authRequest = { accessToken: accessToken, state: state } as ExternalAuthenticationRequest;
      await dispatch(authenticateFromFacebook(authRequest)).unwrap();

      if (props.onSignedIn) {
        props.onSignedIn();
      }
    } catch (err) {
      console.log(err);

      const registerState = await externalLogin.getFacebookRegistrationProps(accessToken, state);
      routeNavigator.goToRegistration({ state: registerState, replace: true });
    }
  };

  useEffect(() => {
    if (clientId !== '') {
      return;
    }

    (async () => {
      let setting = await settingClient.get();

      if (!setting?.authentication?.facebookClientId) {
        return;
      }

      setClientId(setting.authentication.facebookClientId);
    })();
  }, [clientId, settingClient]);

  const signIn = () => {
    setIsBusy(true);

    const state = externalLogin.randomState();
    externalLogin.saveFacebookState(state);

    let redirectUrl = basePath + '/signin-facebook';
    let endpoint =
      'https://www.facebook.com/v21.0/dialog/oauth' +
      `?response_type=token` +
      `&state=${state}` +
      `&redirect_uri=${redirectUrl}` +
      `&client_id=${clientId}` +
      `&scope=email,public_profile`;

    if (appStyleMode === AppStyle.Mobile) {
      signInMobileApp(endpoint);
    } else {
      setRedirectAfterLogin(location.pathname + location.search);
      window.location.replace(endpoint);
    }
  };

  const signInMobileApp = (endpoint: string) => {
    const browser = window.cordova.InAppBrowser.open(endpoint, '_blank', 'location=no');
    let oauthCompleted = false;

    const onLoadStart = async (event: any) => {
      const url: string = event.url;
      const callbackUrl = 'lt.paulini.mobile://oauth_callback';
      if (url.startsWith(callbackUrl) || url.startsWith('http://' + callbackUrl)) {
        oauthCompleted = true;
        browser.removeEventListener('loadstart', onLoadStart);
        browser.close();
        const fragment = url.includes('?') ? url.split('?')[1] : '';
        const params = new URLSearchParams(fragment);
        try {
          await handleFacebookSignIn(params.get('access_token') ?? undefined, params.get('state') ?? undefined);
        } finally {
          setIsBusy(false);
        }
      }
    };
    browser.addEventListener('loadstart', onLoadStart);
    browser.addEventListener('exit', () => {
      if (!oauthCompleted) {
        // user closed browser without completing login
        setIsBusy(false);
      }
    });
  };

  return (
    props.isVisible && (
      <Box>
        <IconButton
          onClick={signIn}
          sx={{
            border: '1px solid black',
            borderRadius: '10px',
            px: '22px',
            py: '12px',
          }}
        >
          <img src={facebook} alt="Facebook" style={{ height: '24px' }} />
        </IconButton>
        <Spinner isActive={isBusy} />
      </Box>
    )
  );
}
